import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { toJS } from 'mobx';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import Mousetrap from 'mousetrap';

import ImageScroller from '../../components/imagescroller';
// Access members at render time only (componentsOther.X) — this registry sits
// in an import cycle and is not fully evaluated at module scope.
import componentsOther from '../../product/components-other';
import MarkdownDisplay from '../../components/gui/markdownDisplay';
import Poll from '../../components/poll';
import FactBox from '../articleRender/factBox';
import ReadAlsoBox from '../articleRender/readAlsoBox';
import { splitBodySegments, addBylinesToImages, fixImageSrc } from '../articleRender/articleContentProcessor';
import ResultsList from '../articleRender/resultsList';
import ProductBox from '../articleRender/productBox';

import localUtil from '../../lib/util';
import { decorateExternalLinks } from '../../lib/externalLinks';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';
import { createHelpSearchLogger } from '../../lib/helpSearchLogger';
import { estimateReadingMinutes, slugifyHeading, uniqueSlug } from '../../lib/helpArticleMeta';
import { orderHelpArticles, pinSort, titleSort } from '../../lib/helpArticleOrder';
import { searchRaces, raceRoute } from '../../lib/raceSearch';

const MARKDOWN_OPTIONS = {
    pedantic: false,
    gfm: true,
    breaks: true,
    sanitize: false,
    smartLists: true,
    smartypants: true,
    xhtml: true,
    highlight: function(code, lang) {
        const hljs = require('highlight.js');
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
    },
    langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

// Minimum query length before the "Spør AI" button is offered (avoids spending tokens on 1-2 char queries).
const AI_SEARCH_MIN_CHARS = 3;
// Collapsed height of a long AI answer before the "Vis mer" toggle is used.
const AI_COLLAPSED_MAX_PX = 220;
// The help-center SEARCH (not the landing list) also covers news and the frontpage "main"
// articles (no category), so a club's welcome article can answer "når er det treninger".
// Member feed posts are deliberately excluded. Mirrors CANDIDATE_CATEGORIES on the backend.
const HELP_SEARCH_CATEGORIES = ['help', 'news'];


// Where a non-help search hit opens. Only category 'help' can be shown in the help pane
// (loadArticle/viewArticle filter by category 'help'); everything else gets its own page.
function helpArticleRoute({ uuidv4, title }) {
    // news and frontpage "main" articles share the article page (same as the header quick-search).
    const slug = encodeURIComponent(title || '').toLocaleLowerCase();
    return `/article/${slug}/${uuidv4}`;
}

// Small badge on search hits that are not help articles, so members know the row leaves the help center.
function helpArticleCategoryBadge(article) {
    if (!article || article.category === 'help') {
        return null;
    }
    const isNews = article.category === 'news';
    return (
        <span class='badge text-bg-secondary ms-2' style='font-size: 0.7em; vertical-align: middle;'>
            {isNews
                ? <Text id='help.article-category-news'>News</Text>
                : <Text id='help.article-category-main'>Frontpage</Text>}
        </span>
    );
}

@observer
class Help extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            articles: [],
            // Race title hits for the current search (race domains only); shown above the articles.
            raceHits: [],
            aiTagging: false,
            aiTaggingAll: false,
            aiTagProgress: null,
            showManageTags: false,
            savingTags: false,
            dragIndex: null,
            newTagName: '',
            editingArticleTags: null,
            savingArticleTags: false,
            dragOverIndex: null,
            expandedSection: null,
            // Help-center search AI agent
            aiLoading: false,
            aiSuggestion: null,
            // Expanded by default (search with no article open); collapses when an article is opened.
            aiExpanded: true,
            // «Var dette nyttig?» block: textarea shown after a thumbs-down, before it is sent;
            // feedbackChanging is true after «Endre» so the question shows again despite an existing vote.
            feedbackComment: '',
            feedbackPendingDown: false,
            feedbackChanging: false,
            feedbackError: false,
            feedbackAdminOpen: false,
            // Table of contents built from h2/h3 in the rendered body (≥ 3 headings).
            toc: [],
            tocOpen: true,
        };
        this.mainContainer = null;
        this.calendar = null;
        this.userCheckTimer = null;
        // Monotonic token used to ignore stale AI responses when the query changes mid-flight.
        this.aiSeq = 0;
        this.focusTimer = null;
        this.searchInputRef = null;
        this.articleBodyRef = null;
        this.mainPaneRef = null;
        this.tocArticleId = 0;
        // True while a query routed in from the header quick-search owns the article list,
        // so background loadAll calls don't clobber the search results.
        this.initialSearchActive = false;
        // Logs the query the user settled on (plus its hit count) so admins can see which
        // questions the help center does not answer. Never logs while the user is still typing.
        this.helpSearchLogger = createHelpSearchLogger({
            log: ({ search, hits }) => {
                const { helpSearchStore } = this.props.stores;
                if (helpSearchStore) {
                    helpSearchStore.logSearch({ search, hits });
                }
            },
        });
    }

    // Open-gap count for the admin badge. Only admins / media workers may list gaps.
    loadGapCount = async () => {
        const { userStore, helpSearchStore } = this.props.stores;
        if (!helpSearchStore || !(userStore.isAdmin || userStore.isMediaWorker)) {
            return;
        }
        await helpSearchStore.loadGaps('open');
    }

    onClickSearchGaps = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('helpSearchGaps', {
            height: drawerHeightLarge,
            callback: () => {
                this.loadGapCount();
                this.loadAll();
            },
        }, drawerLevel + 2);
    }

    expandSection = (e) => {
        const tag = e.currentTarget.dataset.tag;
        this.setState(prev => ({
            expandedSection: prev.expandedSection === tag ? null : tag,
        }), () => {
            if (this.state.expandedSection) {
                const el = document.getElementById(`help-landing-section-${tag}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        });
    }

    loadAll = async () => {
        const { articleStore, raceStore, userStore } = this.props.stores;
        // Use user's isRaceAdmin flag (from user profile) OR raceStore.isRaceAdmin (from current race)
        // This ensures admin articles are shown if the user is a race admin for any race
        // Check for both boolean true and number 1 since MongoDB might store either
        const { user } = userStore;
        const userIsRaceAdmin = user?.isRaceAdmin === true || user?.isRaceAdmin === 1;
        const isRaceAdmin = userIsRaceAdmin || raceStore?.isRaceAdmin;
        const articles = await articleStore.load({
            query: {
                hostname: articleStore.currentHostname,
                category: 'help',
                all: 1,
                sort: 'title',
                isRaceAdmin: isRaceAdmin ? 1 : 0,
            },
            addData: ['tags'],
            // The store default (100) silently truncates — there are 390+ help articles,
            // which cut every title sorting after ~"Hvordan..." from the landing list.
            limit: 1000,
            skipUpdate: true,
        });
        this.setState({
            articles,
        });
    }

    /*
     * The PDF endpoint is public (help articles are readable logged out), so the
     * link needs no jwtToken — but it does need the hostname, which is what the
     * backend scopes the lookup by.
     */
    articlePdfUrl = (articleId) => {
        const { articleStore } = this.props.stores;
        const hostname = encodeURIComponent(articleStore.currentHostname || '');
        return `${util.getApiServer()}/api/articles/${articleId}/pdf?hostname=${hostname}`;
    }

    loadArticle = async id => {
        const { articleStore, userStore, raceStore } = this.props.stores;
        const { user } = userStore;
        const userIsRaceAdmin = user?.isRaceAdmin === true || user?.isRaceAdmin === 1;
        const isRaceAdmin = userIsRaceAdmin || raceStore?.isRaceAdmin;
        await articleStore.load(id, false, { query: {
            hostname: articleStore.currentHostname,
            category: 'help',
            all: 1,
            isRaceAdmin: isRaceAdmin ? 1 : 0,
        }, addData: ['tags', 'polls'] });
        const { article } = articleStore;
        this.trackHelpArticleView(article);
        this.tocArticleId = 0;
        this.setState({
            currentArticle: article,
            feedbackComment: '',
            feedbackPendingDown: false,
            feedbackChanging: false,
            feedbackError: false,
            toc: [],
        });
        this.loadMyFeedback(article);
    }

    // Help articles had no view tracking at all - log article_view so stats accumulate.
    trackHelpArticleView = (article) => {
        const { appState } = this.props.stores;
        if (article?.id) {
            appState.trackArticle('view', article);
        }
    }

    // The feedback block needs the caller's own vote; only logged-in users may vote.
    loadMyFeedback = (article) => {
        const { userStore, articleFeedbackStore } = this.props.stores;
        if (!articleFeedbackStore) return;
        // Clear first so a slow response for the previous article can never show up here;
        // renderFeedback additionally checks that the row belongs to the open article.
        articleFeedbackStore.clearMine();
        const userId = userStore.user?.id;
        if (article?.id && userId) {
            if (userStore.isAdmin || userStore.isMediaWorker) {
                articleFeedbackStore.loadForArticle(article.id, userId);
            } else {
                articleFeedbackStore.loadMine(article.id, userId);
            }
        }
    }

    toggleFeedbackAdmin = () => {
        this.setState(prev => ({ feedbackAdminOpen: !prev.feedbackAdminOpen }));
    }

    onClickMarkFeedbackReviewed = (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        if (id > 0) this.props.stores.articleFeedbackStore.markReviewed(id);
    }

    // Admin / media worker: votes and comments for the open article, right under the vote block.
    renderFeedbackAdmin() {
        const { userStore, articleFeedbackStore } = this.props.stores;
        const { currentArticle, feedbackAdminOpen } = this.state;
        if (!articleFeedbackStore || !(userStore.isAdmin || userStore.isMediaWorker)) return null;
        if (!currentArticle?.id) return null;
        const { articleFeedbacks = [], counts = {} } = articleFeedbackStore;
        const rows = articleFeedbacks.filter(r => r.article === currentArticle.id);
        const locale = userStore.language === 'en' ? 'en-GB' : 'nb-NO';
        return (
            <div class='card mt-3'>
                <div class='card-body py-2'>
                    <button
                        type='button'
                        class='btn btn-link p-0 text-decoration-none fw-bold text-body d-flex align-items-center gap-2 w-100'
                        onClick={this.toggleFeedbackAdmin}
                    >
                        <i class={`fa-solid fa-chevron-${feedbackAdminOpen ? 'down' : 'right'}`} />
                        <Text id='help.feedback-admin-title'>Feedback</Text>
                        <span class='badge text-bg-success'><i class='fa-solid fa-thumbs-up me-1' />{counts.up || 0}</span>
                        <span class='badge text-bg-danger'><i class='fa-solid fa-thumbs-down me-1' />{counts.down || 0}</span>
                    </button>
                    {feedbackAdminOpen && (
                        <div class='mt-2'>
                            {rows.length === 0 && (
                                <small class='text-body-secondary fst-italic'>
                                    <Text id='help.feedback-admin-none'>No feedback yet.</Text>
                                </small>
                            )}
                            {rows.length > 0 && (
                                <div class='list-group'>
                                    {rows.map(r => (
                                        <div key={r.id} class='list-group-item d-flex flex-column gap-1'>
                                            <div class='d-flex flex-wrap align-items-center gap-2'>
                                                <i class={`fa-solid ${r.vote === 'up' ? 'fa-thumbs-up text-success' : 'fa-thumbs-down text-danger'}`} />
                                                <small class='text-body-secondary'>
                                                    {localUtil.formatDate(r.createdDate, { locale, dateStyle: 'medium' })}
                                                    {' · '}
                                                    <Text id='help.feedback-admin-user' fields={{ id: r.user }}>user {r.user}</Text>
                                                </small>
                                                {r.status === 'reviewed' ? (
                                                    <span class='badge text-bg-secondary ms-auto'>
                                                        <Text id='help.feedback-reviewed'>Reviewed</Text>
                                                    </span>
                                                ) : (
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-outline-secondary rounded-pill ms-auto text-nowrap'
                                                        data-id={r.id}
                                                        onClick={this.onClickMarkFeedbackReviewed}
                                                    >
                                                        <i class='fa-solid fa-check' /> <Text id='help.feedback-mark-reviewed'>Mark as reviewed</Text>
                                                    </button>
                                                )}
                                            </div>
                                            {r.comment && <div style='white-space: pre-wrap;'>{r.comment}</div>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    onClickFeedbackUp = async () => {
        const { articleFeedbackStore } = this.props.stores;
        const { currentArticle } = this.state;
        if (!currentArticle?.id) return;
        const response = await articleFeedbackStore.vote({ articleId: currentArticle.id, vote: 'up' });
        if (response?.status === 200 || response?.status === 201) {
            this.setState({
                feedbackPendingDown: false, feedbackComment: '', feedbackChanging: false, feedbackError: false,
            });
        } else {
            this.setState({ feedbackError: true });
        }
    }

    onClickFeedbackDown = () => {
        this.setState({ feedbackPendingDown: true });
    }

    onFeedbackCommentInput = (e) => {
        this.setState({ feedbackComment: e.target.value });
    }

    onSubmitFeedbackDown = async () => {
        const { articleFeedbackStore } = this.props.stores;
        const { currentArticle, feedbackComment } = this.state;
        if (!currentArticle?.id) return;
        const response = await articleFeedbackStore.vote({
            articleId: currentArticle.id, vote: 'down', comment: feedbackComment,
        });
        if (response?.status === 200 || response?.status === 201) {
            this.setState({ feedbackPendingDown: false, feedbackChanging: false, feedbackError: false });
        } else {
            // Keep the textarea (and the typed comment) so the reader can retry.
            this.setState({ feedbackError: true });
        }
    }

    onClickFeedbackChange = () => {
        this.setState({ feedbackChanging: true, feedbackPendingDown: false, feedbackComment: '' });
    }

    // Zero-hit search: open a support ticket with the search text already in the body.
    onClickSearchToTicket = () => {
        const { supportTicketStore } = this.props.stores;
        const { search } = this.state;
        const line = `Jeg søkte etter «${(search || '').trim()}» i hjelpesenteret uten å finne svar.\n\n`;
        // NewTicket resets the body on mount, so the text travels in a dedicated prefill field.
        supportTicketStore.updateKeyValue('newTicketPrefillBody', line);
        route('/support/new');
    }

    // ---- In-page keyboard shortcuts ----
    focusSearchShortcut = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (this.searchInputRef) this.searchInputRef.focus();
    }

    prevArticleShortcut = () => {
        if (this.state.currentArticle?.id) this.goToSibling(-1);
    }

    nextArticleShortcut = () => {
        if (this.state.currentArticle?.id) this.goToSibling(1);
    }

    // Same inputs render() uses for the sidebar, so prev/next follows what the reader sees.
    getOrderedArticles = () => {
        const { appState } = this.props.stores;
        const { search, filter, articles } = this.state;
        const helpCenterTagsStr = appState.currentCustomer?.featureFlags?.helpCenterTags || '';
        const headerTags = helpCenterTagsStr ? helpCenterTagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];
        const filteredArticles = articles?.filter(this.filterArticles) || [];
        const hasActiveFiltersOrSearch = search || Object.keys(filter || {}).length > 0;
        const showGrouped = headerTags.length > 0 && !hasActiveFiltersOrSearch;
        return orderHelpArticles({
            articles: filteredArticles,
            headerTags,
            showGrouped,
            getTags: a => this.getArticleAllTags(a),
        });
    }

    getSiblings = () => {
        const { currentArticle } = this.state;
        if (!currentArticle?.id) return { prev: null, next: null };
        const ordered = this.getOrderedArticles();
        const idx = ordered.findIndex(a => a.id === currentArticle.id);
        if (idx === -1) return { prev: null, next: null };
        return {
            prev: idx > 0 ? ordered[idx - 1] : null,
            next: idx < ordered.length - 1 ? ordered[idx + 1] : null,
        };
    }

    goToSibling = async (delta) => {
        const { prev, next } = this.getSiblings();
        const target = delta < 0 ? prev : next;
        if (!target) return;
        await this.loadArticle(target.id);
        if (this.mainPaneRef) {
            this.mainPaneRef.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    onClickPrev = () => this.goToSibling(-1);

    onClickNext = () => this.goToSibling(1);

    renderPrevNext() {
        const { prev, next } = this.getSiblings();
        if (!prev && !next) return null;
        return (
            <div class='d-flex flex-wrap justify-content-between gap-2 mt-3'>
                <div>
                    {prev && (
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill text-nowrap' onClick={this.onClickPrev}>
                            <i class='fa-solid fa-arrow-left me-1' />
                            <Text id='help.prev-article'>Previous</Text>: {prev.title}
                        </button>
                    )}
                </div>
                <div>
                    {next && (
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill text-nowrap' onClick={this.onClickNext}>
                            <Text id='help.next-article'>Next</Text>: {next.title}
                            <i class='fa-solid fa-arrow-right ms-1' />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Scans the rendered body for h2/h3. Runs once per article id; markdown rendering is
    // synchronous so the headings exist by the time componentDidUpdate fires.
    buildToc = () => {
        const { currentArticle } = this.state;
        const container = this.articleBodyRef;
        if (!currentArticle?.id || !container) {
            if (this.state.toc.length > 0) this.setState({ toc: [] });
            return;
        }
        if (this.tocArticleId === currentArticle.id) return;
        const headings = Array.from(container.querySelectorAll('h2, h3'));
        const used = new Set();
        const toc = headings.map(el => {
            const text = (el.textContent || '').trim();
            if (!el.id) {
                el.id = uniqueSlug(slugifyHeading(text), used);
            } else {
                used.add(el.id);
            }
            return { id: el.id, text, level: el.tagName === 'H2' ? 2 : 3 };
        }).filter(h => h.text);
        this.tocArticleId = currentArticle.id;
        this.setState({ toc, tocOpen: true });
    }

    onClickTocLink = (e) => {
        e.preventDefault();
        const { id } = e.currentTarget.dataset;
        const el = this.articleBodyRef?.querySelector(`[id="${id}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    toggleToc = () => {
        this.setState(prev => ({ tocOpen: !prev.tocOpen }));
    }

    renderToc() {
        const { toc, tocOpen } = this.state;
        if (!toc || toc.length < 3) return null;
        return (
            <div class='card mb-3'>
                <div class='card-body py-2'>
                    <button type='button' class='btn btn-link p-0 text-decoration-none fw-bold text-body' onClick={this.toggleToc}>
                        <i class={`fa-solid fa-chevron-${tocOpen ? 'down' : 'right'} me-2`} />
                        <Text id='help.toc-title'>Contents</Text>
                    </button>
                    {tocOpen && (
                        <ul class='list-unstyled mb-0 mt-2'>
                            {toc.map(heading => (
                                <li key={heading.id} class={heading.level === 3 ? 'ms-3' : ''}>
                                    <a href={`#${heading.id}`} data-id={heading.id} onClick={this.onClickTocLink}>{heading.text}</a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        );
    }

    renderArticleMeta(currentArticle) {
        const { userStore } = this.props.stores;
        const { createdDate, updatedDate, body } = currentArticle || {};
        if (!createdDate && !updatedDate) return null;
        const locale = userStore.language === 'en' ? 'en-GB' : 'nb-NO';
        const fmt = date => localUtil.formatDate(date, { locale, dateStyle: 'medium' });
        const created = createdDate ? fmt(createdDate) : '';
        // Show «Sist oppdatert» only when it falls on a later day than the creation date,
        // so a never-edited article does not print the same date twice.
        const dayMs = 24 * 60 * 60 * 1000;
        const editedLater = createdDate && updatedDate
            && (new Date(updatedDate).getTime() - new Date(createdDate).getTime()) >= dayMs;
        const updated = updatedDate && (editedLater || !createdDate) ? fmt(updatedDate) : '';
        const minutes = estimateReadingMinutes(body);
        const parts = [];
        if (created) {
            parts.push(<Text id='help.meta-created' fields={{ date: created }}>Created {created}</Text>);
        }
        if (updated) {
            parts.push(<Text id='help.meta-updated' fields={{ date: updated }}>Last updated {updated}</Text>);
        }
        if (minutes > 0) {
            parts.push(<Text id='help.meta-reading-time' fields={{ minutes }}>approx. {minutes} min read</Text>);
        }
        return (
            <div class='text-body-secondary small mb-2'>
                <i class='fa-regular fa-clock me-1' />
                {parts.map((part, idx) => (
                    <span key={`meta-${idx}`}>{idx > 0 ? ' · ' : ''}{part}</span>
                ))}
            </div>
        );
    }

    renderFeedback() {
        const { userStore, articleFeedbackStore } = this.props.stores;
        const {
            currentArticle, feedbackComment, feedbackPendingDown, feedbackChanging, feedbackError,
        } = this.state;
        if (!userStore.user?.id || !articleFeedbackStore) return null;
        const { myVote = {}, saving } = articleFeedbackStore;
        // Only a row for THIS article counts; a late response for the previous article is ignored.
        const hasVote = (myVote?.vote === 'up' || myVote?.vote === 'down') && myVote?.article === currentArticle?.id;
        const errorLine = feedbackError ? (
            <div class='text-danger small mt-2'>
                <i class='fa-solid fa-triangle-exclamation me-1' />
                <Text id='help.feedback-error'>Could not save your feedback. Please try again.</Text>
            </div>
        ) : null;

        if (hasVote && !feedbackPendingDown && !feedbackChanging) {
            return (
                <div class='card mt-4'>
                    <div class='card-body d-flex flex-wrap align-items-center gap-2'>
                        <i class={`fa-solid ${myVote.vote === 'up' ? 'fa-thumbs-up text-success' : 'fa-thumbs-down text-danger'}`} />
                        <span>
                            <Text id='help.feedback-thanks'>Thanks for your feedback!</Text>
                        </span>
                        <button type='button' class='btn btn-link btn-sm p-0 ms-auto' onClick={this.onClickFeedbackChange}>
                            <Text id='help.feedback-change'>Change</Text>
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div class='card mt-4'>
                <div class='card-body'>
                    <div class='d-flex flex-wrap align-items-center gap-2'>
                        <strong><Text id='help.feedback-question'>Was this article helpful?</Text></strong>
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-success rounded-pill text-nowrap'
                            onClick={this.onClickFeedbackUp}
                            disabled={saving}
                        >
                            <i class='fa-solid fa-thumbs-up' /> <Text id='help.feedback-yes'>Yes</Text>
                        </button>
                        <button
                            type='button'
                            class={`btn btn-sm ${feedbackPendingDown ? 'btn-danger' : 'btn-outline-danger'} rounded-pill text-nowrap`}
                            onClick={this.onClickFeedbackDown}
                            disabled={saving}
                        >
                            <i class='fa-solid fa-thumbs-down' /> <Text id='help.feedback-no'>No</Text>
                        </button>
                    </div>
                    {feedbackPendingDown && (
                        <div class='mt-3'>
                            <label class='form-label small text-body-secondary'>
                                <Text id='help.feedback-what-missing'>What was missing?</Text>
                            </label>
                            <textarea
                                class='form-control'
                                rows='3'
                                maxLength='2000'
                                value={feedbackComment}
                                onInput={this.onFeedbackCommentInput}
                            />
                            <div class='d-flex justify-content-end mt-2'>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-primary rounded-pill'
                                    onClick={this.onSubmitFeedbackDown}
                                    disabled={saving}
                                >
                                    {saving && <i class='fa-solid fa-spinner fa-spin me-1' />}
                                    <Text id='help.feedback-send'>Send</Text>
                                </button>
                            </div>
                        </div>
                    )}
                    {errorLine}
                </div>
            </div>
        );
    }

    openStatsDrawer = (articleId) => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('articleStats', {
            height: drawerHeightLarge,
            articleId,
        }, drawerLevel + 2);
    }

    openArticleInDrawer = async (id) => {
        const { articleStore } = this.props.stores;
        await articleStore.load(id, false, { query: {
            hostname: articleStore.currentHostname,
            all: 1,
        }, addData: ['tags', 'polls'] });
        const { article } = articleStore;
        this.trackHelpArticleView(article);
        // Opening an article collapses the AI answer (show-less) so the article gets the room.
        this.setState({
            currentArticle: article,
            aiExpanded: false,
        });
    }

    viewArticle = async e => {
        e.preventDefault();
        e.stopPropagation();

        const {
            id, category, uuidv4, title,
        } = e.target.closest('[data-id]').dataset;
        // A search hit (or AI reference) that is not a help article: the in-pane loader below
        // filters by category 'help' and would come back empty, so open its own page instead.
        if (category && category !== 'help' && uuidv4) {
            route(helpArticleRoute({ uuidv4, title }));
            return;
        }
        // const { articles } = this.state;
        // const article = articles.find(a => a.id === idInt);

        // this.setState({
        //     currentArticle: article,
        // });
        const { articleStore, userStore, raceStore } = this.props.stores;
        const { user } = userStore;
        const userIsRaceAdmin = user?.isRaceAdmin === true || user?.isRaceAdmin === 1;
        const isRaceAdmin = userIsRaceAdmin || raceStore?.isRaceAdmin;
        await articleStore.load(id, false, { query: {
            hostname: articleStore.currentHostname,
            category: 'help',
            all: 1,
            isRaceAdmin: isRaceAdmin ? 1 : 0,
        }, addData: ['tags', 'polls'] });
        const { article } = articleStore;
        this.trackHelpArticleView(article);
        console.log('viewArticle', article)
        this.tocArticleId = 0;
        // Opening an article collapses the AI answer (show-less) so the article gets the room.
        this.setState({
            currentArticle: article,
            aiExpanded: false,
            feedbackComment: '',
            feedbackPendingDown: false,
            feedbackChanging: false,
            feedbackError: false,
            toc: [],
        });
        this.loadMyFeedback(article);
    }

    onRelatedChanged = (newList) => {
        const { currentArticle } = this.state;
        this.setState({ currentArticle: { ...currentArticle, relatedArticles: newList } });
    }

    onClickEditArticle = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        // console.log('onClickEditContact', id, parseInt(id, 10));
        const { drawerLevel = 1 } = this.props;
		const { appState, contactStore } = this.props.stores;
		const { drawerHeightLarge } = appState;
		appState.openDrawer('editArticle', {
			height: drawerHeightLarge,
            id,
            callback: article => {
                appState.toggleDrawer(false, drawerLevel + 2);
                this.loadAll();
            },
		}, drawerLevel + 2);
    }

    copyArticleLink = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const url = `${window.location.origin}${window.location.pathname}#/help/${id}`;

        // Copy to clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(() => {
                const { appState } = this.props.stores;
                appState.addToast({
                    type: 'success',
                    message: 'Link copied to clipboard!',
                    icon: 'link',
                });
            }).catch(err => {
                console.error('Failed to copy:', err);
            });
        } else {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = url;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                const { appState } = this.props.stores;
                appState.addToast({
                    type: 'success',
                    message: 'Link copied to clipboard!',
                    icon: 'link',
                });
            } catch (err) {
                console.error('Failed to copy:', err);
            }
            document.body.removeChild(textArea);
        }
    }

    onClickNewArticle = e => {
        const { drawerLevel = 1 } = this.props;
		const { appState, contactsStore } = this.props.stores;
		const { drawerHeightLarge } = appState;
        const { category } = e.target.closest('button').dataset;
		appState.openDrawer('newArticle', {
			height: drawerHeightLarge,
            isNew: true,
            category,
            callback: () => {
                this.loadAll();
            },
		}, drawerLevel + 2);
	}

    renderArticleRow = (article) => {
        const { showManageTags, editingArticleTags, savingArticleTags } = this.state;
        const { appState } = this.props.stores;
        const { currentCustomer } = appState;
        const helpCenterTagsStr = currentCustomer?.featureFlags?.helpCenterTags || '';
        const headerTags = helpCenterTagsStr ? helpCenterTagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];
        const hasVideoWithSubtitles = article.files?.some(file =>
            file.mimetype &&
            file.mimetype.startsWith('video/') &&
            file.transcript &&
            file.transcript.length > 0
        );
        const articleTags = this.getArticleAllTags(article);
        const isEditingThis = editingArticleTags === article.id;

        return (
            <div class='w-100'>
                <div
                    class={`w-100 d-flex flex-row align-items-start py-1 rounded ${article.active ? '' : 'text-muted font-italic'}`}
                    style='cursor: pointer; font-size: 0.9em;'
                    data-id={article.id}
                    data-category={article.category || 'main'}
                    data-uuidv4={article.uuidv4}
                    data-title={article.title}
                    onClick={this.viewArticle}
                >
                    {article.active ? '' : <i class='fa-duotone fa-eye-slash text-danger me-2' />}
                    <span class='me-2 mt-1' style='font-size: 0.5em; color: #aaa;'>&#9679;</span>
                    <span class='flex-fill'>
                        {article.title}
                        {helpArticleCategoryBadge(article)}
                        {showManageTags && articleTags.filter(t => headerTags.includes(t)).map(tag => (
                            <span class='badge text-bg-primary ms-1' style='font-size: 0.7em; vertical-align: middle;'>{tag}</span>
                        ))}
                    </span>
                    {showManageTags && (
                        <button
                            type='button'
                            class={`btn btn-sm ${isEditingThis ? 'btn-primary' : 'btn-outline-secondary'} py-0 px-1 ms-1`}
                            style='font-size: 0.75em; line-height: 1.2;'
                            data-id={article.id}
                            onClick={this.toggleArticleTagEditor}
                            title='Edit article tags'
                        >
                            <i class='fa-solid fa-tags' />
                        </button>
                    )}
                    {savingArticleTags && isEditingThis && (
                        <i class='fa-solid fa-spinner fa-spin ms-1 text-body-secondary' style='font-size: 0.75em;' />
                    )}
                    {article.forRaceAdmins && <i class='fa-duotone fa-user-shield text-warning ms-2 mt-1' title='Only visible to race admins' style='font-size: 0.8em;' />}
                    {article.forCustomerAdmins && <i class='fa-duotone fa-user-shield text-warning ms-2 mt-1' title='Only visible to customer admins' style='font-size: 0.8em;' />}
                    {article.files?.length > 0 && <i class={`fa-duotone fa-film text-body-tertiary ms-1 mt-1`} style='font-size: 0.75em;' />}
                    {hasVideoWithSubtitles && <i class={`fa-solid fa-closed-captioning text-success ms-1 mt-1`} title='Has subtitles' style='font-size: 0.75em;' />}
                </div>
                {showManageTags && isEditingThis && headerTags.length > 0 && (
                    <div class='ms-4 mb-2 p-2 border rounded bg-body-secondary' style='font-size: 0.8em;' onClick={e => e.stopPropagation()}>
                        {headerTags.map(tag => (
                            <div class='form-check form-check-inline'>
                                <input
                                    class='form-check-input'
                                    type='checkbox'
                                    checked={articleTags.includes(tag)}
                                    data-tag={tag}
                                    data-id={article.id}
                                    onChange={this.toggleArticleTag}
                                />
                                <label class='form-check-label'>{tag}</label>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // AI-tags ONLY the currently open article (one Claude call): section tags +
    // related articles. Manual related entries are preserved by the backend.
    onClickAiTagArticles = async () => {
        const { appState, userStore } = this.props.stores;
        const { customer } = userStore;
        const { currentArticle } = this.state;
        if (!currentArticle?.id) return;
        this.setState({ aiTagging: true });
        try {
            const response = await util.fetchApi('/api/ai/tag-help-articles', { method: 'POST' }, {
                customer: customer?.id,
                articleIds: [currentArticle.id],
            });
            if (response?.status >= 400) {
                throw new Error(response.message || `Status ${response.status}`);
            }
            const result = (response?.data || []).find(r => r.id === currentArticle.id);
            if (result?.error) {
                throw new Error(result.error);
            }
            this.setState({
                currentArticle: {
                    ...currentArticle,
                    aiTopicTags: result?.aiTopicTags?.length > 0 ? result.aiTopicTags : currentArticle.aiTopicTags,
                    relatedArticles: result?.relatedArticles || currentArticle.relatedArticles,
                },
            });
            appState.addToast({
                type: 'success',
                message: 'AI tagging complete for this article.',
                icon: 'tag',
            });
        } catch (err) {
            appState.addToast({
                type: 'danger',
                message: `AI tagging failed: ${err.message}`,
                icon: 'exclamation-triangle',
            });
        }
        this.setState({ aiTagging: false });
    }

    // Sweeps ALL help articles, carefully: only articles still missing tags or
    // related articles get an AI call, sent in small chunks so each request stays
    // under the gateway timeout (a single full-sweep request 504s), and the user
    // confirms the number of AI calls before anything starts.
    onClickAiTagAllArticles = async () => {
        const dialogAction = beginDialogAction(this, 'onClickAiTagAllArticles');
        if (!dialogAction) return false;
        try {
            const { appState, userStore, articleStore } = this.props.stores;
            const { customer } = userStore;
            this.setState({ aiTaggingAll: true, aiTagProgress: null });
            try {
                const allArticles = await articleStore.load({
                    query: {
                        hostname: articleStore.currentHostname,
                        category: 'help',
                        all: 1,
                        sort: 'title',
                    },
                    limit: 1000,
                    skipUpdate: true,
                });
                const ids = (allArticles || [])
                    .filter(a => !(a.aiTopicTags?.length > 0) || !(a.relatedArticles?.length > 0))
                    .map(a => a.id)
                    .filter(id => id > 0);
                if (ids.length === 0) {
                    appState.addToast({
                        type: 'success',
                        message: 'All articles already have tags and related articles.',
                        icon: 'tag',
                    });
                    this.setState({ aiTaggingAll: false, aiTagProgress: null });
                    return;
                }
                if (!((await appConfirm(`Dette vil AI-tagge ${ids.length} artikler (artikler som allerede har tags og relaterte artikler hoppes over). Fortsette?`)) && dialogAction.isCurrent())) {
                    this.setState({ aiTaggingAll: false, aiTagProgress: null });
                    return;
                }
                const CHUNK_SIZE = 5;
                let tagged = 0;
                /* eslint-disable no-await-in-loop */
                for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
                    const chunk = ids.slice(i, i + CHUNK_SIZE);
                    const response = await util.fetchApi('/api/ai/tag-help-articles', { method: 'POST' }, {
                        customer: customer?.id,
                        articleIds: chunk,
                    });
                    if (response?.status >= 400) {
                        throw new Error(response.message || `Status ${response.status}`);
                    }
                    tagged += response?.data?.length || 0;
                    this.setState({ aiTagProgress: { done: Math.min(i + CHUNK_SIZE, ids.length), total: ids.length } });
                }
                /* eslint-enable no-await-in-loop */
                appState.addToast({
                    type: 'success',
                    message: `AI tagging complete. Tagged ${tagged} articles.`,
                    icon: 'tag',
                });
                this.loadAll();
            } catch (err) {
                appState.addToast({
                    type: 'danger',
                    message: `AI tagging failed: ${err.message}`,
                    icon: 'exclamation-triangle',
                });
            }
            this.setState({ aiTaggingAll: false, aiTagProgress: null });
        } finally {
            dialogAction.finish();
        }
    }

    stripMarkdown = (text) => {
        if (!text) return '';
        return text
            .replace(/[#*_~`>\[\]()!|-]/g, '')
            .replace(/\n+/g, ' ')
            .trim()
            .substring(0, 100) + (text.length > 100 ? '...' : '');
    }

    goBack = () => {
        // Closing the article returns to the search-only view, where the AI answer shows in full.
        this.tocArticleId = 0;
        this.setState({
            currentArticle: null, aiExpanded: true, feedbackPendingDown: false, feedbackChanging: false, toc: [],
        });
        this.props.stores.articleFeedbackStore?.clearMine();
    }

    clearSearch = () => {
        // Back to the full landing list — allow loadAll to repopulate articles again.
        this.initialSearchActive = false;
        this.aiSeq += 1; // cancel any in-flight AI answer
        this.helpSearchLogger.cancel();
        this.setState({
            search: '',
            raceHits: [],
            aiLoading: false,
            aiSuggestion: null,
            aiExpanded: true,
            currentArticle: null,
        }, () => {
            this.loadAll();
        });
    }

    searchInput = (e) => {
        const search = e.target.value;
        // Typing a new search deselects any open article and drops any AI answer for the old
        // query. The AI only runs when the user clicks "Spør AI" — never automatically.
        this.aiSeq += 1; // cancel any in-flight AI answer
        this.setState({ search, aiLoading: false, aiSuggestion: null, currentArticle: null });

        // Article search debounce — fast, so the result list appears quickly.
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
            this.doSearch();
        }, 700);
    }

    // Run a search arriving from outside (e.g. the header quick-search routing to
    // /help/search/:initialSearch). Mirrors searchInput but fires immediately — no
    // debounce — so the article list populates on arrival. The AI answer stays
    // on-demand ("Spør AI" button) here too.
    applyInitialSearch = (raw) => {
        const search = (raw || '').trim();
        if (!search) {
            return;
        }
        // The search now owns the article list — keep background loadAll calls from clobbering it.
        this.initialSearchActive = true;
        this.aiSeq += 1; // cancel any in-flight AI answer
        clearTimeout(this.searchTimer);
        this.setState({ search, currentArticle: null, aiLoading: false, aiSuggestion: null }, () => {
            this.doSearch();
        });
    }

    doSearch = async () => {
        const { search } = this.state;
        const { articleStore, appState } = this.props.stores;
        // Race lookup (race domains only) runs alongside the article search; a failure
        // just means no race rows.
        const [articles, raceHits] = await Promise.all([
            articleStore.load({
                query: {
                    hostname: articleStore.currentHostname,
                    category: HELP_SEARCH_CATEGORIES,
                    includeMainArticles: 1,
                    stemmedSearch: 1,
                    search,
                    all: 1,
                },
                skipUpdate: true,
            }),
            searchRaces({ search, currentCustomer: appState.currentCustomer }),
        ]);
        this.setState({
            articles,
            raceHits,
        });
        // A race hit answers the search too, so it is not logged as a help gap.
        const articleHits = Array.isArray(articles) ? articles.length : 0;
        this.helpSearchLogger.onSearchResult(search, articleHits + raceHits.length);
    }

    // Runs only when the user clicks "Spør AI" — never automatically while typing.
    runAiSuggestion = async () => {
        const q = (this.state.search || '').trim();
        const { articleStore } = this.props.stores;
        if (q.length < AI_SEARCH_MIN_CHARS) {
            return;
        }
        this.aiSeq += 1;
        const seq = this.aiSeq;
        this.setState({ aiLoading: true });
        try {
            const data = await articleStore.getSearchSuggestion(q);
            if (seq !== this.aiSeq) return; // a newer search superseded this one
            // An empty-answer object (never null) — null means "not asked yet" and shows the
            // button, while an empty answer shows the "no clear answer" text instead.
            // The answer shows in full (expanded); it collapses once an article is opened.
            this.setState({ aiLoading: false, aiSuggestion: data || { suggestedAnswer: null }, aiExpanded: true });
        } catch (err) {
            if (seq !== this.aiSeq) return;
            this.setState({ aiLoading: false, aiSuggestion: { suggestedAnswer: null } });
        }
    }

    toggleAiExpanded = () => {
        this.setState(prev => ({ aiExpanded: !prev.aiExpanded }));
    }


    viewTags = (obj, { useScrollBehavior } = {}) => {
        if (obj.tags?.length === 0 && obj.loans?.length === 0 && !obj.ownedByOther && !obj.url && !obj.parents?.length > 0 && !obj.files?.length > 0) {
            return '';
        }
        const { userStore } = this.props.stores;
        const { filter = {} } = this.state;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        let tagClass = darkmode ? 'dark' : 'light';

        return (<>
            <div class='w-100 overflow-hidden'>
                <div class='box-line-tags d-flex flex-nowrap'>
                    {obj.files?.length > 0 && <>
                        <span class={`badge text-bg-light me-1 mt-1`}>
                            <i class='fa-duotone fa-file' /> <span class='font-weight-lighter'> x {obj.files.length}</span>
                        </span>
                    </>}
                    {obj.tags?.length > 0 && obj.tags.map(tag => {
                        if (filter[tag.name]) {
                            tagClass = 'primary';
                        } else {
                            tagClass = darkmode ? 'dark' : 'light';
                        }
                        return (<>
                            <span
                                class={`badge text-bg-${tagClass} me-1 mt-1`}
                                style='cursor: pointer;'
                                onClick={useScrollBehavior ? this.scrollToSection : this.toggleTagFilter}
                                data-tag={tag.name}
                            >#{tag.name}</span>
                        </>);
                    })}
                </div>
            </div>
        </>);
    }

    scrollToSection = e => {
        e.preventDefault();
        e.stopPropagation();
        const { tag } = e.target.closest('span').dataset;
        const el = document.getElementById(`help-section-${tag}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    toggleTagFilter = e => {
        e.preventDefault();
        e.stopPropagation();
        const { tag } = e.target.closest('span').dataset;
        const { filter = {} } = this.state;
        if (filter[tag]) {
            delete filter[tag];
        } else {
            filter[tag] = true;
        }
        this.setState({
            filter,
        });
    }

    filterArticles = article => {
        const { filter = {} } = this.state;
        if (Object.keys(filter).length === 0) {
            return true;
        }
        let found = false;
        (article.tags || []).forEach(tag => {
            if (filter[tag.name]) {
                found = true;
            }
        });
        return found;
    }

    getArticleAllTags = (article) => {
        const aiTags = article.aiTopicTags || [];
        const regularTagNames = (article.tags || []).map(t => t.name).filter(Boolean);
        const combined = new Set([...aiTags, ...regularTagNames]);
        return [...combined];
    }

    getTagCounts = () => {
        const { articles } = this.state;
        const tagCounts = new Map();
        (articles || []).forEach(article => {
            if (article.active === false) return;
            this.getArticleAllTags(article).forEach(tag => {
                tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
            });
        });
        return tagCounts;
    }

    toggleManageTags = () => {
        this.setState(prev => ({ showManageTags: !prev.showManageTags }));
    }

    saveHelpCenterTags = async (tagsArray) => {
        const { appState, customerStore } = this.props.stores;
        const { currentCustomer } = appState;
        const newTagsStr = tagsArray.join(',');
        this.setState({ savingTags: true });
        await customerStore.saveField(currentCustomer.id, 'featureFlags.helpCenterTags', newTagsStr);
        // Update appState.currentCustomer locally for immediate UI reflection
        const updatedCustomer = {
            ...currentCustomer,
            featureFlags: {
                ...currentCustomer.featureFlags,
                helpCenterTags: newTagsStr,
            },
        };
        appState.updateField('currentCustomer', updatedCustomer);
        this.setState({ savingTags: false });
    }

    toggleHelpCenterTag = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { tag } = e.target.closest('[data-tag]').dataset;
        const { appState } = this.props.stores;
        const { currentCustomer } = appState;
        const helpCenterTagsStr = currentCustomer?.featureFlags?.helpCenterTags || '';
        const currentTags = helpCenterTagsStr ? helpCenterTagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];

        let newTags;
        if (currentTags.includes(tag)) {
            newTags = currentTags.filter(t => t !== tag);
        } else {
            newTags = [...currentTags, tag];
        }
        await this.saveHelpCenterTags(newTags);
    }

    addCustomTag = async () => {
        const { newTagName } = this.state;
        const trimmed = (newTagName || '').trim();
        if (!trimmed) return;

        const { appState } = this.props.stores;
        const { currentCustomer } = appState;
        const helpCenterTagsStr = currentCustomer?.featureFlags?.helpCenterTags || '';
        const currentTags = helpCenterTagsStr ? helpCenterTagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];

        if (currentTags.includes(trimmed)) {
            this.setState({ newTagName: '' });
            return;
        }

        await this.saveHelpCenterTags([...currentTags, trimmed]);
        this.setState({ newTagName: '' });
    }

    handleNewTagInput = (e) => {
        this.setState({ newTagName: e.target.value });
    }

    handleNewTagKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.addCustomTag();
        }
    }

    toggleArticleTagEditor = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('[data-id]').dataset;
        const idInt = parseInt(id, 10);
        this.setState(prev => ({
            editingArticleTags: prev.editingArticleTags === idInt ? null : idInt,
        }));
    }

    toggleArticleTag = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { tag, id } = e.target.closest('[data-tag]').dataset;
        const articleId = parseInt(id, 10);
        const { articles } = this.state;
        const article = (articles || []).find(a => a.id === articleId);
        if (!article) return;

        const currentTags = article.aiTopicTags || [];
        let newTags;
        if (currentTags.includes(tag)) {
            newTags = currentTags.filter(t => t !== tag);
        } else {
            newTags = [...currentTags, tag];
        }

        this.setState({ savingArticleTags: true });
        await util.fetchApi('/api/articles/' + articleId, { method: 'PATCH' }, { 'aiTopicTags-overwrite': newTags });

        // Update local articles state immediately
        const updatedArticles = (articles || []).map(a => {
            if (a.id === articleId) {
                return { ...a, aiTopicTags: newTags };
            }
            return a;
        });
        this.setState({ articles: updatedArticles, savingArticleTags: false });
    }

    handleTagDragStart = (e) => {
        const { index } = e.target.closest('[data-index]').dataset;
        this.setState({ dragIndex: parseInt(index, 10), dragOverIndex: null });
        e.dataTransfer.effectAllowed = 'move';
    }

    handleTagDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const el = e.target.closest('[data-index]');
        if (!el) return;
        const overIndex = parseInt(el.dataset.index, 10);
        if (this.state.dragOverIndex !== overIndex) {
            this.setState({ dragOverIndex: overIndex });
        }
    }

    handleTagDragEnd = () => {
        this.setState({ dragIndex: null, dragOverIndex: null });
    }

    handleTagDrop = async (e) => {
        e.preventDefault();
        const { dragIndex } = this.state;
        const el = e.target.closest('[data-index]');
        if (!el) {
            this.setState({ dragIndex: null, dragOverIndex: null });
            return;
        }
        const dropIndex = parseInt(el.dataset.index, 10);

        if (dragIndex === null || dragIndex === dropIndex) {
            this.setState({ dragIndex: null, dragOverIndex: null });
            return;
        }

        const { appState } = this.props.stores;
        const { currentCustomer } = appState;
        const helpCenterTagsStr = currentCustomer?.featureFlags?.helpCenterTags || '';
        const tags = helpCenterTagsStr ? helpCenterTagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];

        const draggedTag = tags[dragIndex];
        tags.splice(dragIndex, 1);
        tags.splice(dropIndex, 0, draggedTag);

        this.setState({ dragIndex: null, dragOverIndex: null });
        await this.saveHelpCenterTags(tags);
    }

    getReorderedTags = (tags) => {
        const { dragIndex, dragOverIndex } = this.state;
        if (dragIndex === null || dragOverIndex === null || dragIndex === dragOverIndex) return tags;
        const reordered = [...tags];
        const dragged = reordered.splice(dragIndex, 1)[0];
        reordered.splice(dragOverIndex, 0, dragged);
        return reordered;
    }

    componentDidMount() {
        // Check if we have an :id or :initialSearch parameter from the route
        const { id, initialSearch } = this.props;
        // Set synchronously (before checkUserLoaded) so a race-admin loadAll can't clobber the search.
        this.initialSearchActive = !!initialSearch;

        this.checkUserLoaded();

        // In-page shortcuts. Mousetrap ignores keys typed inside inputs, so the search box is unaffected.
        // Bindings are global, and Help also mounts as a drawer (footer/index.js) on top of the /help
        // route; only the route instance binds, so closing the drawer cannot unbind the page's keys.
        if (!this.props.drawerLevel) {
            Mousetrap.bind('/', this.focusSearchShortcut);
            Mousetrap.bind('[', this.prevArticleShortcut);
            Mousetrap.bind(']', this.nextArticleShortcut);
        }

        if (initialSearch) {
            // Arrived from the header quick-search — run the search (article list + AI answer).
            // applyInitialSearch -> doSearch populates the article list, so we skip the full loadAll
            // here: both setState `articles`, and a racing loadAll would overwrite the search results.
            this.applyInitialSearch(initialSearch);
        } else if (id) {
            this.loadAll();
            this.loadArticle(id);
        } else {
            this.loadAll();
            // Autofocus the search box when entering the help center (unless deep-linking to an article).
            // Small delay so focus survives the drawer open animation.
            this.focusTimer = setTimeout(() => {
                if (this.searchInputRef) this.searchInputRef.focus();
            }, 200);
        }
        decorateExternalLinks(this.mainContainer);
    }

    checkUserLoaded = () => {
        const { userStore } = this.props.stores;
        const { user } = userStore;
        if (user?.id) {
            this.loadGapCount();
        }
        // If user is loaded and has isRaceAdmin (check for truthy value including number 1), reload articles
        if (user?.id && (user?.isRaceAdmin === true || user?.isRaceAdmin === 1)) {
            // Don't reload the full list while an arriving quick-search owns the article list.
            if (!this.initialSearchActive) {
                this.loadAll();
            }
        } else if (!user?.id) {
            // User not loaded yet, retry after a short delay
            this.userCheckTimer = setTimeout(() => {
                this.checkUserLoaded();
            }, 500);
        }
    }

    componentWillUnmount() {
        if (this.userCheckTimer) {
            clearTimeout(this.userCheckTimer);
        }
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        if (this.focusTimer) {
            clearTimeout(this.focusTimer);
        }
        if (!this.props.drawerLevel) {
            Mousetrap.unbind('/');
            Mousetrap.unbind('[');
            Mousetrap.unbind(']');
        }
        this.helpSearchLogger.cancel();
    }

    componentDidUpdate(prevProps) {
        // Handle route changes when :id parameter changes
        const { id, initialSearch } = this.props;
        if (initialSearch && initialSearch !== prevProps.initialSearch) {
            // Navigated to a new quick-search query while already on the help center.
            this.applyInitialSearch(initialSearch);
        } else if (id && id !== prevProps.id) {
            this.loadArticle(id);
        }
        this.buildToc();
        decorateExternalLinks(this.mainContainer);
    }

    renderVideoFile = (file) => {
        const poster = file.s3LinkStreamPreview1;
        if (file.s3LinkStream && componentsOther.VideoStreamingPlayer) {
            return (
                <figure class='mb-0'>
                    <div class='video-container rounded-lg'>
                        <componentsOther.VideoStreamingPlayer
                            key={file.s3LinkStream}
                            videoStreamUrl={file.s3LinkStream}
                            stores={this.props.stores}
                            poster={poster}
                            transcript={file.transcript}
                            transcriptionSegments={file.transcriptionSegments}
                            transcriptionLanguage={file.transcriptionLanguage}
                            transcriptEnglish={file.transcriptEnglish}
                            transcriptionSegmentsEnglish={file.transcriptionSegmentsEnglish}
                            transcriptSpanish={file.transcriptSpanish}
                            transcriptionSegmentsSpanish={file.transcriptionSegmentsSpanish}
                            transcriptFrench={file.transcriptFrench}
                            transcriptionSegmentsFrench={file.transcriptionSegmentsFrench}
                            transcriptFinnish={file.transcriptFinnish}
                            transcriptionSegmentsFinnish={file.transcriptionSegmentsFinnish}
                        />
                    </div>
                    {(file.caption || file.photographer) && (
                        <figcaption class='text-body-secondary small mt-1 px-3'>
                            {file.caption}{file.caption && file.photographer && ' '}{file.photographer && <span class='fst-italic'>Foto: {file.photographer}</span>}
                        </figcaption>
                    )}
                </figure>
            );
        }
        if (file.s3Link && file.s3Link.match(/\.(m4v|m4a|mp4)$/i)) {
            return (
                <figure class='mb-0'>
                    <div className='video-container rounded-lg'>
                        <video controls className='video-player'>
                            <source src={file.s3Link} type='video/mp4' />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                    {(file.caption || file.photographer) && (
                        <figcaption class='text-body-secondary small mt-1 px-3'>
                            {file.caption}{file.caption && file.photographer && ' '}{file.photographer && <span class='fst-italic'>Foto: {file.photographer}</span>}
                        </figcaption>
                    )}
                </figure>
            );
        }
        if (file.s3Link && file.s3Link.match(/\.(mov)$/i)) {
            return (
                <figure class='mb-0'>
                    <div className='video-container rounded-lg'>
                        <video controls className='video-player'>
                            <source src={file.s3Link} type='video/quicktime' />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                    {(file.caption || file.photographer) && (
                        <figcaption class='text-body-secondary small mt-1 px-3'>
                            {file.caption}{file.caption && file.photographer && ' '}{file.photographer && <span class='fst-italic'>Foto: {file.photographer}</span>}
                        </figcaption>
                    )}
                </figure>
            );
        }
        return null;
    }

    renderAiOverview = () => {
        const { aiLoading, aiSuggestion, aiExpanded } = this.state;
        // Strip the [Title](/article/uuid) link syntax from the prose — referenced articles are
        // shown as explicit, in-pane clickable shortcuts below the answer instead.
        const answer = aiSuggestion?.suggestedAnswer
            ? aiSuggestion.suggestedAnswer.replace(/\[([^\]]+)\]\(\/article\/[a-f0-9-]+\)/g, '$1')
            : '';
        const refs = (aiSuggestion?.referencedArticles || []).filter(r => r.id > 0);
        // Long answers collapse behind a "Vis mer" toggle so they don't dominate the pane (esp. above an article).
        const answerLong = answer.trim().length > 300 || (answer.match(/\n/g) || []).length >= 6;
        const collapsed = answerLong && !aiExpanded;

        return (
            <div class='w-100 d-flex flex-column align-items-center mt-5 pt-4 px-4'>
                <div class='w-100' style='max-width: 820px;'>
                    <div class='card border-0 shadow-sm rounded-4 bg-body'>
                        <div class='card-body p-4'>
                            <div class='d-flex align-items-center mb-3'>
                                <span
                                    class='d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary me-2'
                                    style='width: 2rem; height: 2rem;'
                                >
                                    <i class='fa-solid fa-robot' />
                                </span>
                                <h6 class='fw-bold mb-0 text-body'>AI-svar</h6>
                            </div>

                            {aiLoading ? (
                                <div class='d-flex align-items-center text-body-secondary'>
                                    <span class='spinner-grow spinner-grow-sm me-1' role='status' style='width: 0.5rem; height: 0.5rem; animation-delay: 0s;' />
                                    <span class='spinner-grow spinner-grow-sm me-1' role='status' style='width: 0.5rem; height: 0.5rem; animation-delay: 0.15s;' />
                                    <span class='spinner-grow spinner-grow-sm me-2' role='status' style='width: 0.5rem; height: 0.5rem; animation-delay: 0.3s;' />
                                    <span class='fst-italic'>Tenker...</span>
                                </div>
                            ) : answer ? (<>
                                <div
                                    class='help-ai-answer text-body position-relative'
                                    style={collapsed ? `max-height: ${AI_COLLAPSED_MAX_PX}px; overflow: hidden;` : ''}
                                >
                                    <MarkdownDisplay markdown={answer} markedOpts={localUtil.getMarkdownOptions()} stores={this.props.stores} />
                                    {collapsed && (
                                        <div
                                            class='position-absolute bottom-0 start-0 w-100'
                                            style='height: 3rem; background: linear-gradient(to bottom, rgba(0,0,0,0), var(--bs-body-bg)); pointer-events: none;'
                                        />
                                    )}
                                </div>
                                {answerLong && (
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-link text-decoration-none p-0 mt-2'
                                        onClick={this.toggleAiExpanded}
                                    >
                                        {aiExpanded
                                            ? <>Vis mindre <i class='fa-solid fa-chevron-up ms-1' /></>
                                            : <>Vis mer <i class='fa-solid fa-chevron-down ms-1' /></>}
                                    </button>
                                )}
                                {refs.length > 0 && (
                                    <div class='mt-3 pt-3 border-top'>
                                        <small class='fw-bold text-body-secondary text-uppercase' style='font-size: 0.7em; letter-spacing: 0.05em;'>Relaterte artikler</small>
                                        <div class='d-flex flex-column align-items-start gap-1 mt-2'>
                                            {refs.map(r => (
                                                <button
                                                    key={r.id}
                                                    type='button'
                                                    class='btn btn-sm btn-link text-start text-decoration-none p-0'
                                                    data-id={r.id}
                                                    data-category={r.category || 'main'}
                                                    data-uuidv4={r.uuidv4}
                                                    data-title={r.title}
                                                    onClick={this.viewArticle}
                                                >
                                                    <i class='fa-solid fa-arrow-right me-2 text-primary' />
                                                    {r.title}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div class='mt-3'>
                                    <small class='text-body-tertiary fst-italic'>
                                        <i class='fa-solid fa-circle-info me-1' />
                                        AI-generert svar. Se gjerne artiklene i listen til venstre.
                                    </small>
                                </div>
                            </>) : aiSuggestion ? (
                                <div class='text-body-secondary'>
                                    Fant ikke et tydelig svar på dette. Se artiklene i listen til venstre.
                                </div>
                            ) : (
                                <div>
                                    <div class='text-body-secondary mb-3'>
                                        Vil du ha et AI-svar på søket ditt? Svaret lages ut fra hjelpeartiklene.
                                    </div>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-primary rounded-pill'
                                        onClick={this.runAiSuggestion}
                                    >
                                        <i class='fa-solid fa-wand-magic-sparkles me-1' /> Spør AI
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {} = this.props;
        const { currentArticle, search, filter, articles, aiTagging, raceHits = [] } = this.state;
        const { appState, userStore, articleStore } = this.props.stores;
        const { user = {}, isAdmin, isMediaWorker, isVeterinary } = userStore;
        const { currentCustomer } = appState;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const locale = userStore.language === 'en' ? 'en-GB' : 'nb-NO';

        // Section header grouping - use domain customer (appState.currentCustomer) for helpCenterTags
        const helpCenterTagsStr = currentCustomer?.featureFlags?.helpCenterTags || '';
        const headerTags = helpCenterTagsStr ? helpCenterTagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];
        const filteredArticles = articles?.filter(this.filterArticles) || [];
        const hasActiveFiltersOrSearch = search || Object.keys(filter || {}).length > 0;
        const showGrouped = headerTags.length > 0 && !hasActiveFiltersOrSearch;
        // While a meaningful search is active, the right pane shows the AI overview instead of the landing hero.
        const aiActive = (search || '').trim().length >= AI_SEARCH_MIN_CHARS;


        const popularArticles = [...(articles || [])]
            .filter(a => a.active !== false && a.totalViews > 0)
            .sort((a, b) => pinSort(a, b) || ((b.totalViews || 0) - (a.totalViews || 0)))
            .slice(0, 6);

        // Group articles by headerTags for landing page sections
        const getAllTags = (a) => this.getArticleAllTags(a);
        const landingSections = headerTags.map(tag => {
            const sectionArticles = (articles || [])
                .filter(a => a.active !== false && getAllTags(a).includes(tag))
                .sort(pinSort);
            return { tag, articles: sectionArticles };
        }).filter(s => s.articles.length > 0 || isAdmin || isMediaWorker);

        // Uncategorized articles for "Annet" section on landing page
        const categorizedIds = new Set();
        headerTags.forEach(tag => {
            (articles || []).forEach(a => {
                if (a.active !== false && getAllTags(a).includes(tag)) categorizedIds.add(a.id);
            });
        });
        const uncategorizedArticles = (articles || [])
            .filter(a => a.active !== false && !categorizedIds.has(a.id))
            .sort(pinSort);

        return (<>
            <div class={`w-100 h-100 d-flex justify-content-center`} ref={c => { this.mainContainer = c; }}>
                <div class={`w-100 h-100 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`} style='padding-top: 0px; padding-bottom: 0px;'>
                    <div class={`w-100 h-100 d-flex flex-row help-center-layout ${currentArticle ? 'help-reading' : ''}`}>
                        <div class='w-25 h-100 d-flex flex-column overflow-auto position-relative px-3 py-3 bg-light rounded-lg help-pane-nav'>
                            <div class='d-flex flex-wrap align-items-center gap-1 mb-2'>
                                {currentArticle && <>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-secondary rounded-pill text-nowrap'
                                        onClick={this.goBack}
                                    >
                                        <i class='fa-solid fa-arrow-left' /> Tilbake
                                    </button>
                                </>}
                                {(isAdmin || isMediaWorker) && <>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-primary rounded-pill text-nowrap ms-auto'
                                        onClick={this.onClickAiTagAllArticles}
                                        disabled={aiTagging || this.state.aiTaggingAll}
                                        title='AI-tag alle artikler som mangler tags eller relaterte artikler'
                                    >
                                        {this.state.aiTaggingAll ? <>
                                            <i class='fa-solid fa-spinner fa-spin' /> {this.state.aiTagProgress
                                                ? `Tagging ${this.state.aiTagProgress.done}/${this.state.aiTagProgress.total}...`
                                                : 'Tagging...'}
                                        </> : <>
                                            <i class='fa-solid fa-wand-magic-sparkles text-primary' /> AI Tag alle
                                        </>}
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-secondary rounded-pill position-relative text-nowrap'
                                        onClick={this.onClickSearchGaps}
                                        title='Søk medlemmer har gjort uten å finne svar'
                                    >
                                        <i class='fa-solid fa-magnifying-glass-minus text-muted' /> <Text id='help.search-gaps'>Unanswered searches</Text>
                                        {this.props.stores.helpSearchStore?.openCount > 0 && (
                                            <span class='badge rounded-pill text-bg-danger ms-1'>{this.props.stores.helpSearchStore.openCount}</span>
                                        )}
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-secondary rounded-pill text-nowrap'
                                        data-category={'help'}
                                        onClick={this.onClickNewArticle}
                                    >
                                        <i class='fa-solid fa-file text-muted' /> <Text id='help.new-article'>New Article</Text>
                                    </button>
                                    {!currentCustomer?.featureFlags?.helpCenterTagsInherited && <button
                                        type='button'
                                        class={`btn btn-sm ${this.state.showManageTags ? 'btn-primary' : 'btn-outline-secondary'} rounded-pill text-nowrap`}
                                        onClick={this.toggleManageTags}
                                    >
                                        <i class='fa-solid fa-tags' /> Manage Tags
                                    </button>}
                                </>}
                            </div>
                            <div class='w-100 mt-2'>
                                <div class='input-group'>
                                    <span class='input-group-text rounded-pill-left'>
                                        <i class='fa-solid fa-magnifying-glass'></i>
                                    </span>
                                    <input
                                        ref={c => { this.searchInputRef = c; }}
                                        class={`form-control form-control-lg ${search ? '' : 'rounded-pill-right'}`}
                                        type='text'
                                        value={search}
                                        placeholder={'Søk etter hjelpeartikler'}
                                        onInput={this.searchInput}
                                    />
                                    {search && <>
                                        <button class='btn btn-danger btn-sm rounded-pill-right' type='button' onClick={this.clearSearch}>
                                            <i class='fa-solid fa-circle-xmark'></i>
                                        </button>
                                    </>}
                                </div>
                            </div>

                            {this.state.showManageTags && (isAdmin || isMediaWorker)
                                && !currentCustomer?.featureFlags?.helpCenterTagsInherited && (() => {
                                const tagCounts = this.getTagCounts();
                                const allAiTags = [...tagCounts.keys()].sort((a, b) => (tagCounts.get(b) || 0) - (tagCounts.get(a) || 0));
                                const availableTags = allAiTags.filter(t => !headerTags.includes(t));
                                return (
                                    <div class='w-100 mt-3 p-2 border rounded bg-body'>
                                        {this.state.savingTags && (
                                            <div class='text-center mb-2'>
                                                <small class='text-body-secondary'><i class='fa-solid fa-spinner fa-spin' /> Saving...</small>
                                            </div>
                                        )}
                                        <div class='mb-3'>
                                            <small class='fw-bold text-body-secondary text-uppercase' style='font-size: 0.7em; letter-spacing: 0.05em;'>Add Custom Tag</small>
                                            <div class='input-group input-group-sm mt-1'>
                                                <input
                                                    class='form-control form-control-sm'
                                                    type='text'
                                                    placeholder='New tag name...'
                                                    value={this.state.newTagName}
                                                    onInput={this.handleNewTagInput}
                                                    onKeyDown={this.handleNewTagKeyDown}
                                                />
                                                <button
                                                    class='btn btn-sm btn-outline-primary'
                                                    type='button'
                                                    onClick={this.addCustomTag}
                                                    disabled={!this.state.newTagName?.trim()}
                                                >
                                                    <i class='fa-solid fa-plus' /> Add
                                                </button>
                                            </div>
                                        </div>
                                        <div class='mb-3'>
                                            <small class='fw-bold text-body-secondary text-uppercase' style='font-size: 0.7em; letter-spacing: 0.05em;'>Active Section Tags</small>
                                            <div class='d-flex flex-wrap gap-1 mt-1'>
                                                {headerTags.length === 0 && (
                                                    <small class='text-body-secondary fst-italic'>No tags selected</small>
                                                )}
                                                {this.getReorderedTags(headerTags).map((tag, idx) => (
                                                    <span
                                                        class='badge text-bg-primary d-flex align-items-center'
                                                        style={`cursor: grab; transition: transform 0.15s ease; ${this.state.dragIndex !== null && this.state.dragOverIndex === idx ? 'transform: scale(1.1);' : ''} ${this.state.dragIndex === idx && this.state.dragOverIndex === null ? 'opacity: 0.4;' : ''}`}
                                                        draggable='true'
                                                        data-index={idx}
                                                        data-tag={tag}
                                                        onDragStart={this.handleTagDragStart}
                                                        onDragOver={this.handleTagDragOver}
                                                        onDrop={this.handleTagDrop}
                                                        onDragEnd={this.handleTagDragEnd}
                                                    >
                                                        <i class='fa-solid fa-grip-vertical me-1' style='font-size: 0.7em; opacity: 0.6;' />
                                                        {tag}
                                                        <small class='ms-1 opacity-75'>({tagCounts.get(tag) || 0})</small>
                                                        <i
                                                            class='fa-solid fa-xmark ms-1'
                                                            style='cursor: pointer; font-size: 0.8em;'
                                                            data-tag={tag}
                                                            onClick={this.toggleHelpCenterTag}
                                                        />
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <small class='fw-bold text-body-secondary text-uppercase' style='font-size: 0.7em; letter-spacing: 0.05em;'>Available Tags</small>
                                            <div class='d-flex flex-wrap gap-1 mt-1'>
                                                {availableTags.length === 0 && allAiTags.length === 0 && (
                                                    <small class='text-body-secondary fst-italic'>No AI tags found. Run 'AI Tag' first to tag articles.</small>
                                                )}
                                                {availableTags.length === 0 && allAiTags.length > 0 && (
                                                    <small class='text-body-secondary fst-italic'>All tags are active</small>
                                                )}
                                                {availableTags.map(tag => (
                                                    <span
                                                        class='badge text-bg-light'
                                                        style='cursor: pointer;'
                                                        data-tag={tag}
                                                        onClick={this.toggleHelpCenterTag}
                                                    >
                                                        {tag}
                                                        <small class='ms-1 opacity-75'>({tagCounts.get(tag) || 0})</small>
                                                        <i class='fa-solid fa-plus ms-1' style='font-size: 0.7em;' />
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div class='w-100 mt-3'>
                                {this.viewTags({ tags: articleStore.tags }, { useScrollBehavior: showGrouped })}
                            </div>

                            {(search || '').trim().length > 0 && raceHits.length > 0 && (
                                <div class='w-100 mt-3'>
                                    <div class='mb-1'>
                                        <small class='fw-bold text-body-secondary text-uppercase' style='font-size: 0.75em; letter-spacing: 0.05em;'>
                                            <i class='fa-regular fa-flag-checkered me-1' />
                                            <Text id='help.race-hits'>Races</Text>
                                        </small>
                                    </div>
                                    {raceHits.map(race => (
                                        <a
                                            key={race.id}
                                            href={raceRoute(race)}
                                            class='w-100 d-flex flex-row align-items-start py-1 rounded text-body text-decoration-none'
                                            style='font-size: 0.9em;'
                                        >
                                            <span class='me-2 mt-1' style='font-size: 0.5em; color: #aaa;'>&#9679;</span>
                                            <span class='flex-fill'>
                                                {race.title}
                                                {race.isCancelled && (
                                                    <span class='badge text-bg-danger ms-2' style='font-size: 0.7em; vertical-align: middle;'>
                                                        <Text id='help.race-cancelled'>Cancelled</Text>
                                                    </span>
                                                )}
                                                {race.startDate && (
                                                    <small class='text-body-secondary ms-2'>
                                                        {localUtil.formatDate(race.startDate, { locale, dateStyle: 'medium' })}
                                                    </small>
                                                )}
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            )}

                            <div class='w-100 mt-3'>
                                {showGrouped ? <>
                                    {headerTags.map(tag => {
                                        const tagArticles = filteredArticles.filter(a => this.getArticleAllTags(a).includes(tag))
                                            .sort(titleSort);
                                        if (tagArticles.length === 0 && !this.state.showManageTags) return null;
                                        return (<>
                                            <div id={`help-section-${tag}`} class='mt-4 mb-1'>
                                                <small
                                                    class='fw-bold text-body-secondary text-uppercase'
                                                    style='font-size: 0.75em; letter-spacing: 0.05em; cursor: pointer;'
                                                    data-tag={tag}
                                                    onClick={this.expandSection}
                                                >
                                                    {tag} <i class={`fa-solid fa-chevron-${this.state.expandedSection === tag ? 'down' : 'right'} ms-1`} style='font-size: 0.7em;' />
                                                </small>
                                            </div>
                                            <div>
                                                {tagArticles.map(article => this.renderArticleRow(article))}
                                                {tagArticles.length === 0 && this.state.showManageTags && (
                                                    <small class='text-body-secondary fst-italic ms-3' style='font-size: 0.8em;'>No articles tagged. Use <i class='fa-solid fa-tags' /> on articles below to assign.</small>
                                                )}
                                            </div>
                                        </>);
                                    })}
                                    {(() => {
                                        const usedIds = new Set();
                                        headerTags.forEach(tag => {
                                            filteredArticles.forEach(a => {
                                                if (this.getArticleAllTags(a).includes(tag)) usedIds.add(a.id);
                                            });
                                        });
                                        const otherArticles = filteredArticles.filter(a => !usedIds.has(a.id))
                                            .sort(titleSort);
                                        if (otherArticles.length === 0) return null;
                                        return (<>
                                            <div id='help-section-Annet' class='mt-4 mb-1'>
                                                <small
                                                    class='fw-bold text-body-secondary text-uppercase'
                                                    style='font-size: 0.75em; letter-spacing: 0.05em; cursor: pointer;'
                                                    data-tag='Annet'
                                                    onClick={this.expandSection}
                                                >
                                                    Annet <i class={`fa-solid fa-chevron-${this.state.expandedSection === 'Annet' ? 'down' : 'right'} ms-1`} style='font-size: 0.7em;' />
                                                </small>
                                            </div>
                                            <div>
                                                {otherArticles.map(article => this.renderArticleRow(article))}
                                            </div>
                                        </>);
                                    })()}
                                </> : <>
                                    {[...filteredArticles].sort(titleSort).map(article => this.renderArticleRow(article))}
                                </>}

                                {articles?.length > 0 && filteredArticles.length === 0 && <>
                                    <div class='w-100 d-flex flex-row justify-content-center' style='font-size: 3.0em;'>
                                        <i class='fa-duotone fa-question text-muted' />
                                    </div>
                                    <div class='w-100 d-flex flex-row justify-content-center font-italic text-muted mt-3'>
                                        <Text id='help.no-articles'>No articles found</Text>
                                    </div>
                                    {user?.id > 0 && (search || '').trim().length > 0 && (
                                        <div class='w-100 d-flex flex-column align-items-center mt-3'>
                                            <small class='text-body-secondary mb-2'>
                                                <Text id='help.no-answer-prompt'>Didn't find an answer?</Text>
                                            </small>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-primary rounded-pill text-nowrap'
                                                onClick={this.onClickSearchToTicket}
                                            >
                                                <i class='fa-solid fa-life-ring me-1' />
                                                <Text id='help.no-answer-create-ticket'>Create a support request</Text>
                                            </button>
                                        </div>
                                    )}
                                </>}
                            </div>
                        </div>

                        <div class='w-75 h-100 d-flex flex-column overflow-auto position-relative px-3 py-3 help-pane-main' ref={c => { this.mainPaneRef = c; }}>
                            {/* Mobile-only back button: on phones the nav pane (with its own back button) is hidden while reading. */}
                            {currentArticle && (
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary rounded-pill d-lg-none align-self-start mb-2'
                                    onClick={this.goBack}
                                >
                                    <i class='fa-solid fa-arrow-left' /> Tilbake
                                </button>
                            )}
                            {/* During an active search the AI overview pins to the top of the pane — above the landing hero or an opened article. */}
                            {aiActive && this.renderAiOverview()}
                            {!currentArticle && !aiActive && <>
                                <div class='d-flex flex-column align-items-center mt-5 pt-4 px-4 help-landing'>
                                    <div class='text-center mb-5'>
                                        <h2 class='fw-bold text-body'>Hjelpesenter</h2>
                                        <p class='text-body-secondary mt-2'>Finn svar på spørsmål, les guider og lær mer.</p>
                                    </div>

                                    <div class='w-100' style='max-width: 900px;'>
                                        {landingSections.length > 0 ? <>
                                            {landingSections.map(section => {
                                                const isExpanded = this.state.expandedSection === section.tag;
                                                const visibleArticles = isExpanded ? section.articles : section.articles.slice(0, 3);
                                                return (
                                                <div class='mb-5' id={`help-landing-section-${section.tag}`}>
                                                    <h5
                                                        class='fw-bold text-body mb-3 text-uppercase'
                                                        style='cursor: pointer;'
                                                        data-tag={section.tag}
                                                        onClick={this.expandSection}
                                                    >
                                                        {section.tag} <i class={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} ms-1`} style='font-size: 0.7em;' />
                                                    </h5>
                                                    {section.articles.length === 0 && (
                                                        <p class='text-body-secondary fst-italic' style='font-size: 0.85em;'>No articles in this section yet. Assign articles using Manage Tags.</p>
                                                    )}
                                                    <div class='row g-3'>
                                                        {visibleArticles.map(article => (
                                                            <div class='col-md-4' data-id={article.id} onClick={this.viewArticle}>
                                                                <div class='card h-100 border rounded-3' style='cursor: pointer;'>
                                                                    <div class='card-body p-4'>
                                                                        <div class='mb-3'>
                                                                            <svg width='40' height='28' viewBox='0 0 40 28'>
                                                                                <circle cx='14' cy='14' r='12' fill='#e5e5e5' />
                                                                                <circle cx='26' cy='14' r='12' fill='#d0d0d0' />
                                                                            </svg>
                                                                        </div>
                                                                        <h6 class='card-title fw-semibold'>{article.title}</h6>
                                                                        <small class='text-body-secondary'>{this.stripMarkdown(article.body)}</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {section.articles.length > 3 && (
                                                        <div class='mt-3 text-center'>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                                                data-tag={section.tag}
                                                                onClick={this.expandSection}
                                                            >
                                                                {isExpanded ? 'Vis mindre' : `Vis flere (${section.articles.length})`}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                            {uncategorizedArticles.length > 0 && (() => {
                                                const isAnnetExpanded = this.state.expandedSection === 'Annet';
                                                const visibleUncategorized = isAnnetExpanded ? uncategorizedArticles : uncategorizedArticles.slice(0, 3);
                                                return (
                                                <div class='mb-5' id='help-landing-section-Annet'>
                                                    <h5
                                                        class='fw-bold text-body mb-3 text-uppercase'
                                                        style='cursor: pointer;'
                                                        data-tag='Annet'
                                                        onClick={this.expandSection}
                                                    >
                                                        Annet <i class={`fa-solid fa-chevron-${isAnnetExpanded ? 'up' : 'down'} ms-1`} style='font-size: 0.7em;' />
                                                    </h5>
                                                    <div class='row g-3'>
                                                        {visibleUncategorized.map(article => (
                                                            <div class='col-md-4' data-id={article.id} onClick={this.viewArticle}>
                                                                <div class='card h-100 border rounded-3' style='cursor: pointer;'>
                                                                    <div class='card-body p-4'>
                                                                        <div class='mb-3'>
                                                                            <svg width='40' height='28' viewBox='0 0 40 28'>
                                                                                <circle cx='14' cy='14' r='12' fill='#e5e5e5' />
                                                                                <circle cx='26' cy='14' r='12' fill='#d0d0d0' />
                                                                            </svg>
                                                                        </div>
                                                                        <h6 class='card-title fw-semibold'>{article.title}</h6>
                                                                        <small class='text-body-secondary'>{this.stripMarkdown(article.body)}</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {uncategorizedArticles.length > 3 && (
                                                        <div class='mt-3 text-center'>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                                                data-tag='Annet'
                                                                onClick={this.expandSection}
                                                            >
                                                                {isAnnetExpanded ? 'Vis mindre' : `Vis flere (${uncategorizedArticles.length})`}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })()}
                                        </> : <>
                                            {popularArticles.length > 0 && <>
                                                <h5 class='fw-bold text-body mb-3 text-uppercase'>Mest leste artikler</h5>
                                                <div class='row g-3'>
                                                    {popularArticles.map(article => (
                                                        <div class='col-md-4' data-id={article.id} onClick={this.viewArticle}>
                                                            <div class='card h-100 border rounded-3' style='cursor: pointer;'>
                                                                <div class='card-body p-4'>
                                                                    <div class='mb-3'>
                                                                        <svg width='40' height='28' viewBox='0 0 40 28'>
                                                                            <circle cx='14' cy='14' r='12' fill='#e5e5e5' />
                                                                            <circle cx='26' cy='14' r='12' fill='#d0d0d0' />
                                                                        </svg>
                                                                    </div>
                                                                    <h6 class='card-title fw-semibold'>{article.title}</h6>
                                                                    <small class='text-body-secondary'>{this.stripMarkdown(article.body)}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>}
                                        </>}
                                    </div>
                                </div>
                            </>}
                            {currentArticle && <>
                                <div class='mt-2'>
                                    <div class='position-absolute' style='top: 12px; right: 45px;'>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-secondary rounded-pill me-2'
                                            onClick={this.copyArticleLink}
                                            data-id={currentArticle.id}
                                            title='Copy link to this article'
                                        >
                                            <i class='fa-solid fa-link text-muted' /> Copy Link
                                        </button>
                                        {(isAdmin || currentArticle.user === user.id) && (
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-secondary rounded-pill me-2'
                                                onClick={() => this.openStatsDrawer(currentArticle.id)}
                                                title='Show detailed statistics'
                                            >
                                                <i class='fa-solid fa-chart-simple text-muted' /> Stats
                                            </button>
                                        )}
                                        {(isAdmin || (isMediaWorker && currentArticle.customer === appState.currentCustomer?.id)) && <>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-primary rounded-pill me-2'
                                                onClick={this.onClickAiTagArticles}
                                                disabled={aiTagging || this.state.aiTaggingAll}
                                                title='AI-tag kun denne artikkelen'
                                            >
                                                {aiTagging ? <>
                                                    <i class='fa-solid fa-spinner fa-spin' /> Tagging...
                                                </> : <>
                                                    <i class='fa-solid fa-wand-magic-sparkles text-primary' /> AI Tag
                                                </>}
                                            </button>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                                onClick={this.onClickEditArticle}
                                                data-id={currentArticle.id}
                                            >
                                                {/* <i class='fa-solid fa-list text-muted' /> <Text id='dyrejournal.product.all'>All Products</Text> */}
                                                <i class='fa-solid fa-file text-muted' /> <Text id='help.edit-article'>Edit Article</Text>
                                            </button>
                                        </>}
                                    </div>

                                    <div class='d-flex flex-column mt-4'>
                                        {currentArticle.files?.length > 0 && (() => {
                                            const videoFiles = currentArticle.files.filter(f =>
                                                f.s3LinkStream || (f.s3Link && f.s3Link.match(/\.(m4v|m4a|mp4|mov)$/i))
                                            );
                                            const hasInlineVideos = currentArticle.body && /\{\{video\.\d+\}\}/.test(currentArticle.body);
                                            if (hasInlineVideos) {
                                                const inlineIndices = new Set();
                                                const inlineRegex = /\{\{video\.(\d+)\}\}/g;
                                                let m;
                                                while ((m = inlineRegex.exec(currentArticle.body)) !== null) {
                                                    inlineIndices.add(parseInt(m[1], 10));
                                                }
                                                return videoFiles.map((file, vidIdx) => {
                                                    if (inlineIndices.has(vidIdx)) return null;
                                                    return this.renderVideoFile(file);
                                                });
                                            }
                                            return videoFiles.map(file => this.renderVideoFile(file));
                                        })()}
                                        {(() => {
                                            const hasVideo = currentArticle.files?.some(f => f.s3LinkStream || (f.s3Link && f.s3Link.match(/\.(m4v|m4a|mp4|mov)$/i)));
                                            const allVideosInline = hasVideo && currentArticle.body && (() => {
                                                const videoFiles = currentArticle.files.filter(f => f.s3LinkStream || (f.s3Link && f.s3Link.match(/\.(m4v|m4a|mp4|mov)$/i)));
                                                const inlineIndices = new Set();
                                                const inlineRegex = /\{\{video\.(\d+)\}\}/g;
                                                let m;
                                                while ((m = inlineRegex.exec(currentArticle.body)) !== null) {
                                                    inlineIndices.add(parseInt(m[1], 10));
                                                }
                                                return videoFiles.every((_, idx) => inlineIndices.has(idx));
                                            })();
                                            const showTopVideos = hasVideo && !allVideosInline;
                                            if (!showTopVideos) {
                                                return <ImageScroller images={currentArticle.images || []} stores={this.props.stores} showImg={img => localUtil.displayImg(img, this.props, '1920x')} />;
                                            }
                                            return null;
                                        })()}
                                    </div>

                                    <h1>{currentArticle.title}</h1>
                                    {this.renderArticleMeta(currentArticle)}

                                    <div class='d-flex flex-row mb-3'>
                                        <a
                                            class='btn btn-sm btn-outline-secondary rounded-pill'
                                            href={this.articlePdfUrl(currentArticle.id)}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            <i class='fa-solid fa-file-pdf text-muted' /> <Text id='help.download-pdf'>Download this help article as PDF</Text>
                                        </a>
                                    </div>

                                    {this.renderToc()}
                                    <div class='d-flex flex-column help-article-body' ref={c => { this.articleBodyRef = c; }}>
                                        {(() => {
                                            const { language } = userStore;
                                            const segments = splitBodySegments(currentArticle.body);
                                            const hasPollMarker = segments.some(s => s.type === 'poll');
                                            const hasMarkers = segments.length > 1 || segments[0]?.type !== 'markdown';
                                            const hasBylines = currentArticle.images?.some(img => img?.caption || img?.photographer);

                                            if (!hasMarkers && !hasBylines) {
                                                if (!currentArticle.polls?.[0]) {
                                                    return <MarkdownDisplay markdown={fixImageSrc(localUtil.replaceImages(currentArticle.body, currentArticle.images, language, false))} markedOpts={localUtil.getMarkdownOptions()} stores={this.props.stores} />;
                                                }
                                                const paragraphs = currentArticle.body.split(/\n\n+/).filter(p => p.trim());
                                                const insertAfter = Math.max(1, Math.ceil(paragraphs.length / 2));
                                                const firstHalf = paragraphs.slice(0, insertAfter).join('\n\n');
                                                const secondHalf = paragraphs.slice(insertAfter).join('\n\n');
                                                return (<>
                                                    <MarkdownDisplay markdown={fixImageSrc(localUtil.replaceImages(firstHalf, currentArticle.images, language, false))} markedOpts={localUtil.getMarkdownOptions()} stores={this.props.stores} />
                                                    <Poll poll={currentArticle.polls[0]} stores={this.props.stores} />
                                                    {secondHalf && (
                                                        <MarkdownDisplay markdown={fixImageSrc(localUtil.replaceImages(secondHalf, currentArticle.images, language, false))} markedOpts={localUtil.getMarkdownOptions()} stores={this.props.stores} />
                                                    )}
                                                </>);
                                            }

                                            const pollInsertIdx = !hasPollMarker && currentArticle.polls?.[0]
                                                ? Math.max(1, Math.ceil(segments.length / 2))
                                                : -1;
                                            return (<>
                                                {segments.map((segment, idx) => {
                                                    const rendered = [];
                                                    if (segment.type === 'markdown') {
                                                        let processed = fixImageSrc(localUtil.replaceImages(segment.content, currentArticle.images, language, false));
                                                        if (hasBylines) {
                                                            processed = addBylinesToImages(processed, currentArticle.images);
                                                        }
                                                        rendered.push(<MarkdownDisplay key={`seg-${idx}`} markdown={processed} markedOpts={localUtil.getMarkdownOptions()} stores={this.props.stores} />);
                                                    } else if (segment.type === 'factbox') {
                                                        rendered.push(<FactBox key={`fb-${segment.articleId}`} articleId={segment.articleId} stores={this.props.stores} />);
                                                    } else if (segment.type === 'lesogsa') {
                                                        rendered.push(<ReadAlsoBox key={`la-${segment.articleId}`} articleId={segment.articleId} stores={this.props.stores} onOpenArticle={this.openArticleInDrawer} />);
                                                    } else if (segment.type === 'poll' && currentArticle.polls?.[0]) {
                                                        rendered.push(<Poll key={`poll-${idx}`} poll={currentArticle.polls[0]} stores={this.props.stores} />);
                                                    } else if (segment.type === 'video') {
                                                        const videoFiles = currentArticle.files?.filter(f =>
                                                            f.s3LinkStream || (f.s3Link && f.s3Link.match(/\.(m4v|m4a|mp4|mov)$/i))
                                                        ) || [];
                                                        const videoFile = videoFiles[segment.videoIndex];
                                                        if (videoFile) {
                                                            rendered.push(<div key={`video-${idx}`} class='my-3'>{this.renderVideoFile(videoFile)}</div>);
                                                        }
                                                    } else if (segment.type === 'results') {
                                                        const rl = currentArticle.resultsLists?.[segment.resultsIndex];
                                                        if (rl) {
                                                            rendered.push(<ResultsList key={`results-${idx}`} resultsList={rl} stores={this.props.stores} />);
                                                        }
                                                    } else if (segment.type === 'product') {
                                                        rendered.push(<ProductBox key={`prod-${segment.productId}`} productId={segment.productId} stores={this.props.stores} />);
                                                    }
                                                    if (pollInsertIdx === idx + 1) {
                                                        rendered.push(<Poll key='poll-auto' poll={currentArticle.polls[0]} stores={this.props.stores} />);
                                                    }
                                                    return rendered;
                                                })}
                                            </>);
                                        })()}
                                    </div>
                                </div>
                                <div class='mt-2'>
                                    {this.viewTags(currentArticle)}
                                </div>
                                {this.renderFeedback()}
                                {this.renderFeedbackAdmin()}
                                {this.renderPrevNext()}
                                {/* C component via the registry (absent in the Dyrejournal build). */}
                                {componentsOther.RelatedArticles && <componentsOther.RelatedArticles
                                    stores={this.props.stores}
                                    article={currentArticle}
                                    canEdit={isAdmin || isMediaWorker}
                                    onOpenArticle={this.openArticleInDrawer}
                                    onChanged={this.onRelatedChanged}
                                />}
                            </>}
                        </div>

                    </div>

                </div>
            </div>
        </>);
    }
}

export default Help;
